import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, DollarSign, TrendingUp, Stethoscope, Briefcase, AlertCircle, ArrowRight, ExternalLink, Calculator, Scale } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const HowMuchIsCaseWorthPage = () => {
  const faqData = [
    {
      question: "What is the average car accident settlement in Washington?",
      answer: "Settlements vary dramatically based on injury severity, from a few thousand dollars for minor injuries to hundreds of thousands or more for serious injuries. There's no meaningful 'average' because every case is different."
    },
    {
      question: "How do insurance companies calculate settlement amounts?",
      answer: "Insurance companies add up your medical bills, lost wages, and property damage, then apply formulas for pain and suffering. They typically offer less than your case is worth, hoping you'll accept without negotiating."
    },
    {
      question: "Do I need to wait until I'm fully recovered to know my case value?",
      answer: "Ideally, yes. You should reach 'maximum medical improvement' (MMI) before settling. This is when your doctor says you've recovered as much as you're going to. Settling too early risks leaving money on the table."
    },
    {
      question: "Does having a lawyer increase my settlement amount?",
      answer: "Research says yes. Insurance Research Council studies show that claimants with attorneys receive settlements averaging 3.5 times higher than those without representation, even after attorney fees."
    },
    {
      question: "What if the at-fault driver doesn't have enough insurance?",
      answer: "Your own underinsured motorist (UIM) coverage may apply. This is coverage you pay for that protects you when the other driver's insurance isn't enough. I can help you understand all available sources of compensation."
    }
  ];

  const factors = [
    {
      category: "Medical Expenses",
      icon: Stethoscope,
      items: [
        "Emergency room and hospital bills",
        "Doctor visits and specialist appointments",
        "Physical therapy and rehabilitation",
        "Prescription medications",
        "Medical equipment (crutches, braces, etc.)",
        "Estimated future medical costs"
      ]
    },
    {
      category: "Lost Income",
      icon: Briefcase,
      items: [
        "Wages lost while recovering",
        "Used sick days or vacation time",
        "Lost bonuses or commissions",
        "Reduced earning capacity",
        "Lost business opportunities",
        "Future lost income if disability is permanent"
      ]
    },
    {
      category: "Pain and Suffering",
      icon: AlertCircle,
      items: [
        "Physical pain (past and ongoing)",
        "Emotional distress and anxiety",
        "Loss of enjoyment of life",
        "Impact on relationships",
        "Scarring or disfigurement",
        "Permanent disability or impairment"
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="how-much-is-case-worth-page">
      <SEOHead 
        title="How Much Is My Car Accident Case Worth in Washington? | Amy C. Brown Law"
        description="Wondering what your car accident claim is worth in Washington? Learn the factors that determine settlement value. Free case evaluation: (425) 675-1224."
        faqData={faqData}
        serviceData={{
          name: "Car Accident Case Evaluation",
          description: "Free case evaluation and settlement estimation for car accident victims in Washington State. Understanding the full value of your claim.",
          category: "Personal Injury Law - Case Valuation"
        }}
      />

      {/* Hero Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <DollarSign className="w-4 h-4" />
                Case Valuation
              </div>
              
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-secondary mb-6 leading-tight">
                How Much Is My Car Accident Case Worth?
              </h1>
              
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                This is one of the most common questions I hear. And I understand why—you're dealing with medical bills, maybe you can't work, and you need to know what to expect.
              </p>

              <div className="bg-accent-soft rounded-xl p-6 mb-8 border-l-4 border-primary">
                <p className="text-slate-700 leading-relaxed">
                  The honest answer: it depends on your specific injuries, treatment, and circumstances. But I can explain the factors that determine value—and help you understand whether you're being offered a fair amount.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a 
                  href="tel:425-675-1224"
                  className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl"
                  data-testid="hero-call-btn"
                >
                  <Phone className="w-5 h-5" />
                  Get a Free Evaluation
                </a>
                <Link 
                  to="/compensation-estimator"
                  className="inline-flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold text-lg hover:bg-secondary hover:text-white transition-all"
                >
                  <Calculator className="w-5 h-5" />
                  Try the Calculator
                </Link>
              </div>

              <div className="flex flex-wrap gap-6 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Free case evaluation
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  No fees unless you win
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-100">
              <h3 className="font-heading text-xl font-bold text-secondary mb-6">Key Value Factors:</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-primary">1</div>
                  <p className="text-slate-700">Severity and type of your injuries</p>
                </div>
                <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-primary">2</div>
                  <p className="text-slate-700">Total medical treatment and costs</p>
                </div>
                <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-primary">3</div>
                  <p className="text-slate-700">Lost income and earning capacity</p>
                </div>
                <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-primary">4</div>
                  <p className="text-slate-700">Available insurance coverage</p>
                </div>
                <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-primary">5</div>
                  <p className="text-slate-700">Clarity of fault/liability</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Factors Breakdown */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl font-bold text-secondary mb-4 text-center">
            What Goes Into Your Case Value
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Your settlement should compensate you for all losses—not just your current medical bills.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {factors.map((factor, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-8">
                <factor.icon className="w-10 h-10 text-primary mb-4" />
                <h3 className="font-heading text-xl font-semibold text-secondary mb-4">{factor.category}</h3>
                <ul className="space-y-2">
                  {factor.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-slate-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-secondary mb-8 text-center">
            Does Hiring a Lawyer Actually Help?
          </h2>
          
          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              This is a fair question. You're already dealing with bills—do you really want to pay attorney fees on top of everything?
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              Independent research gives a clear answer. The Insurance Research Council—which is funded by the insurance industry itself—has studied this question extensively.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-primary text-white rounded-xl p-6">
              <TrendingUp className="w-10 h-10 mb-4" />
              <h3 className="font-heading text-3xl font-bold mb-2">3.5x Higher</h3>
              <p className="text-primary-foreground/90">Average settlement for represented claimants vs. unrepresented, according to Insurance Research Council studies.</p>
              <p className="text-xs mt-4 text-primary-foreground/70">Source: IRC "Paying for Auto Injuries" study</p>
            </div>
            <div className="bg-secondary text-white rounded-xl p-6">
              <Scale className="w-10 h-10 mb-4" />
              <h3 className="font-heading text-3xl font-bold mb-2">85%</h3>
              <p className="text-slate-300">Percentage of bodily injury insurance payouts that go to claimants with attorney representation.</p>
              <p className="text-xs mt-4 text-slate-400">Source: Insurance Research Council data</p>
            </div>
          </div>

          <div className="bg-accent-soft rounded-xl p-6 border-l-4 border-primary">
            <p className="text-slate-700 leading-relaxed">
              <strong>The math works:</strong> If your case settles for 3.5 times more with an attorney, and attorney fees are typically 33%, you still net significantly more than handling it yourself. That's why insurance companies prefer to deal with unrepresented claimants.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator CTA */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 text-center">
            <Calculator className="w-16 h-16 text-primary mx-auto mb-6" />
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Get an Estimate of Your Case Value
            </h2>
            <p className="text-slate-600 mb-8 max-w-xl mx-auto">
              Our free compensation estimator can give you a rough idea of what your case might be worth based on your injuries, medical costs, and lost wages.
            </p>
            <Link 
              to="/compensation-estimator"
              className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all"
            >
              <Calculator className="w-5 h-5" />
              Try the Compensation Estimator
            </Link>
            <p className="text-sm text-slate-500 mt-4">
              Or call me for a personalized evaluation: <a href="tel:425-675-1224" className="text-primary font-semibold">(425) 675-1224</a>
            </p>
          </div>
        </div>
      </section>

      {/* Personal Note */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-primary">
            <h2 className="font-heading text-2xl font-bold text-secondary mb-4">
              Why I Give Honest Evaluations
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              I'll tell you something many lawyers won't: not every case is worth pursuing. Sometimes the injuries are minor, the liability is unclear, or the available insurance is limited.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              When you call me, I'll give you an honest assessment. If your case has significant value, I'll explain why. If it doesn't, I'll tell you that too—and I won't waste your time.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Having worked on the insurance defense side, I know what cases are worth. I use that knowledge to fight for fair compensation—and to give you realistic expectations.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Common Questions About Case Value
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-slate-50 p-6 rounded-xl">
                <h3 className="font-heading font-semibold text-secondary mb-3">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Concepts - Entity SEO */}
      <section className="py-12 bg-slate-50 border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Understanding Case Valuation
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a href="https://en.wikipedia.org/wiki/Damages" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Legal Damages <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Pain_and_suffering" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Pain and Suffering <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Contingent_fee" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Contingency Fee <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Settlement_(litigation)" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Legal Settlement <ExternalLink className="w-3 h-3" />
              </span>
            </a>
          </div>
          <p className="text-xs text-slate-400 text-center mt-4">
            Learn more about these concepts on Wikipedia
          </p>
        </div>
      </section>

      {/* Related Car Accident Resources */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Related Car Accident Resources
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/car-crashes" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <DollarSign className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Car Accident Claims</p>
                <p className="text-sm text-slate-500">Overview of the car accident claim process</p>
              </div>
            </Link>
            <Link to="/should-i-accept-settlement" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <CheckCircle className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Settlement Offers</p>
                <p className="text-sm text-slate-500">How to evaluate what they're offering</p>
              </div>
            </Link>
            <Link to="/when-to-hire-car-accident-lawyer" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <Scale className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">When to Hire a Lawyer</p>
                <p className="text-sm text-slate-500">Signs you'd benefit from legal help</p>
              </div>
            </Link>
            <Link to="/everett-car-accident-lawyer" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <ArrowRight className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Everett Car Accident Lawyer</p>
                <p className="text-sm text-slate-500">Local representation in Snohomish County</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-6">
            Want to Know What Your Case Is Worth?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Call me for a free, honest evaluation. No pressure, no obligation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowMuchIsCaseWorthPage;
